import React, { useState } from "react";
import { OperatorInfo } from "../interfaces/OperatorsBalanceDashboard";
import { Box, Collapse, Paper, Table, TableBody, TableCell, TableContainer, TableRow, Typography, IconButton, Tooltip, Link } from "@mui/material";
import { balanceCell, monitoringIcon, noData, tableHead, tableTitle } from "./TableElements";
import { formatPriceParts } from "../payout-dashboard/PayoutDashboardHelper";
import { InstitutionAttributesMap } from "../interfaces/Institution";
import LaunchIcon from '@mui/icons-material/Launch';
import { generateEscrowLemonwayUrl } from "./OperatorsBalanceDashboardHelper";

export const OPERATOR_TRANSFER_HEADERS = ["Operator", "Currency", "Escrow amount", "Pending transfers", "Status"];
const ALIGNMENTS: ("left" | "center" | "right" | "right" | "center")[] = ["left", "center", "right", "right", "center"];

export const OPERATOR_CURRENCY_MAP: Record<string, string> = {
    BE: 'EUR', BG: 'EUR', DE: 'EUR', ES: 'EUR', FR: 'EUR', IE: 'EUR', IT: 'EUR', NL: 'EUR', PT: 'EUR', SK: 'EUR',
    CH: 'CHF', CZ: 'CZK', GB: 'GBP', HU: 'HUF', PL: 'PLN', RO: 'RON'
};

type OperatorDashboardEscrowTableProps = {
    escrowInfos: OperatorInfo[],
    attributes?: InstitutionAttributesMap | null
}

export const OperatorsBalanceDashboardEscrowTable = ({ escrowInfos, attributes }: OperatorDashboardEscrowTableProps) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <Box sx={{ mt: 3 }}>
            {tableTitle('Escrow Monitoring', isOpen, setIsOpen)}
            <Collapse in={isOpen} timeout="auto">
                {escrowInfos.length === 0 ? noData() : (
                    <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'grey.300', borderTop: 'none', borderRadius: '0 0 4px 4px' }}>
                        <Table size="small">
                            {tableHead(OPERATOR_TRANSFER_HEADERS, ALIGNMENTS)}
                            <TableBody>
                                {escrowInfos.map((row, key) => {
                                    const currency = OPERATOR_CURRENCY_MAP[row.operator] || 'EUR';
                                    const { value, symbol } = formatPriceParts(row.total_to_transfer, currency);
                                    const isApiError = row.escrow_balance === -1;
                                    const isWarning = !isApiError && row.total_to_transfer > row.escrow_balance;
                                    
                                    const lwUrl = generateEscrowLemonwayUrl(attributes, row.operator, currency);

                                    return (
                                        <TableRow hover key={key} sx={isWarning ? { bgcolor: '#FFEDED' } : {}}>
                                            <TableCell align="left" sx={{ pl: 3 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', width: 'fit-content' }}>
                                                    <Typography sx={{ fontWeight: 'bold', minWidth: '35px' }}>
                                                        {row.operator}
                                                    </Typography>
                                                    
                                                    <Box sx={{ width: 24, display: 'flex', justifyContent: 'center' }}>
                                                        {lwUrl && (
                                                            <Tooltip title="View Escrow on Lemonway">
                                                                <IconButton 
                                                                    component={Link} 
                                                                    href={lwUrl} 
                                                                    target="_blank" 
                                                                    size="small" 
                                                                    sx={{ p: 0, color: 'primary.main', opacity: 0.7, '&:hover': { opacity: 1 } }}
                                                                >
                                                                    <LaunchIcon sx={{ fontSize: 14 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </TableCell>

                                            <TableCell align="center">
                                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>{currency}</Typography>
                                            </TableCell>

                                            <TableCell align="right">
                                                {balanceCell(row.escrow_balance, currency)}
                                            </TableCell>

                                            <TableCell align="right">
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', gap: 0.5, fontVariantNumeric: 'tabular-nums' }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
                                                    <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: '25px', textAlign: 'left' }}>{symbol}</Typography>
                                                </Box>
                                            </TableCell>

                                            <TableCell align="center">
                                                {monitoringIcon(isWarning, isApiError)}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Collapse>
        </Box>
    );
}